import { downloadBlob, iamApi } from './client'

export interface BillingPlanDetails {
  planCode: string
  name: string
  description?: string
  currency: string
  billingCycle: string
  baseAmountPaise: number
  baseAmountInr: number
  includedUsers: number
  perUserAmountPaise: number
  perUserAmountInr: number
  gstPercent: number
  active: boolean
  // Usage quotas for this tier — null means unlimited.
  maxTicketsPerMonth?: number | null
  maxClients?: number | null
  maxUsers?: number | null
  maxAssets?: number | null
  maxAiRequestsPerMonth?: number | null
  maxKbArticles?: number | null
  analyticsLookbackMonths?: number | null
  // How long a new org's trial lasts — only used on the free_trial plan.
  trialDays?: number | null
  // Explicit tier order — the source of truth for classifying a plan change
  // as an upgrade or a downgrade. Never infer direction from price.
  tierRank: number
}

export interface SubscriptionDetails {
  id: string
  organizationId: string
  planCode: string
  plan?: BillingPlanDetails
  status: string
  billingCycle: string
  currency: string
  billingEmail?: string
  billingName?: string
  billingAddress?: string
  gstNumber?: string
  billableUsers: number
  // The current billing cycle's end — upgrades don't change it; a scheduled
  // downgrade/cancellation takes effect here.
  nextInvoiceDate?: string
  // Only meaningful when status === 'trial'.
  trialEndsAt?: string
  // A queued downgrade or cancellation — the live plan/status above stay
  // unchanged until pendingChangeEffectiveAt (normally nextInvoiceDate).
  pendingPlanCode?: string | null
  pendingChangeType?: 'downgrade' | 'cancellation' | null
  pendingChangeEffectiveAt?: string | null
}

export interface BillingLineItem {
  description: string
  quantity: number
  unitAmountPaise: number
  amountPaise: number
}

export interface BillingCalculation {
  planCode: string
  currency: string
  billingCycle: string
  periodStart: string
  periodEnd: string
  billableUsers: number
  includedUsers: number
  extraUsers: number
  subtotalInr: number
  gstPercent: number
  gstAmountInr: number
  totalAmountInr: number
  lineItems?: BillingLineItem[]
  totalAmountPaise: number
}

export interface BillingInvoiceDetails {
  id: string
  invoiceNo: string
  organizationId: string
  planCode: string
  status: string
  paymentStatus: string
  paymentMode: string
  currency: string
  periodStart: string
  periodEnd: string
  dueDate: string
  billableUsers: number
  totalAmountInr: number
  balanceDueInr: number
  billingEmail?: string
  emailStatus: string
  // Snapshot of each party's registered legal details at generation time —
  // never re-derived from the org's current profile after the fact.
  billToName?: string
  billToAddress?: string
  billToGstNumber?: string
  billToPanNumber?: string
  billedByName?: string
  billedByAddress?: string
  billedByGstNumber?: string
  billedByPanNumber?: string
}

function base(orgId: string) {
  return `/api/v1/iam/organizations/${encodeURIComponent(orgId)}/billing`
}

export const billingApi = {
  plans: () => iamApi.get<BillingPlanDetails[]>('/api/v1/iam/billing/plans'),
  subscription: (orgId: string) => iamApi.get<SubscriptionDetails>(`${base(orgId)}/subscription`),
  // Billing-contact metadata only (email/name/address/GST/notes/cycle) — no
  // longer changes plan or status. Use upgrade/scheduleDowngrade/cancel below.
  saveSubscription: (orgId: string, payload: Partial<SubscriptionDetails> & { planCode: string }) =>
    iamApi.put<SubscriptionDetails>(`${base(orgId)}/subscription`, payload),
  // Immediate, any time, any number of tiers.
  upgrade: (orgId: string, planCode: string) =>
    iamApi.post<SubscriptionDetails>(`${base(orgId)}/subscription/upgrade`, { planCode }),
  // Queued — takes effect at nextInvoiceDate, not immediately.
  scheduleDowngrade: (orgId: string, planCode: string) =>
    iamApi.post<SubscriptionDetails>(`${base(orgId)}/subscription/schedule-downgrade`, { planCode }),
  // Clears whichever pending change (downgrade or cancellation) is queued.
  cancelPendingChange: (orgId: string) =>
    iamApi.delete<SubscriptionDetails>(`${base(orgId)}/subscription/pending-change`),
  // Queues a cancellation — access continues until nextInvoiceDate.
  cancelSubscription: (orgId: string) =>
    iamApi.post<SubscriptionDetails>(`${base(orgId)}/subscription/cancel`, {}),
  preview: (orgId: string, payload: Record<string, unknown> = {}) =>
    iamApi.post<BillingCalculation>(`${base(orgId)}/preview`, payload),
  invoices: (orgId: string) => iamApi.get<BillingInvoiceDetails[]>(`${base(orgId)}/invoices`),
  generateInvoice: (orgId: string, payload: Record<string, unknown>) =>
    iamApi.post<BillingInvoiceDetails>(`${base(orgId)}/invoices`, payload),
  markPaid: (orgId: string, invoiceId: string, payload: Record<string, unknown> = {}) =>
    iamApi.post<BillingInvoiceDetails>(`${base(orgId)}/invoices/${encodeURIComponent(invoiceId)}/mark-paid`, payload),
  downloadInvoice: (orgId: string, invoiceId: string, invoiceNo?: string) =>
    downloadBlob(`${base(orgId)}/invoices/${encodeURIComponent(invoiceId)}/download`, `${invoiceNo || 'subscription-invoice'}.pdf`),
}

// Platform-wide per-service price list. Superadmin-only, and only usable
// while the active org context is the platform's own "multiaccess" org —
// enforced server-side, not just hidden in this client.
export interface ServicePricingEntry {
  moduleKey: string
  name: string
  monthlyCostPaise: number
  currency: string
  active: boolean
  updatedBy?: string
  creationTime?: string
  updateTime?: string
}

export const servicePricingApi = {
  list: () => iamApi.get<ServicePricingEntry[]>('/api/v1/iam/service-pricing'),
  upsert: (payload: { moduleKey: string; name: string; monthlyCostPaise: number; currency?: string; active: boolean }) =>
    iamApi.put<ServicePricingEntry>('/api/v1/iam/service-pricing', payload),
}

// Superadmin-only plan-tier management — separate from billingApi.plans()
// (org-facing, active-only, no write path). Reuses BillingPlanDetails for
// both the list response and the upsert payload, same as servicePricingApi
// reuses ServicePricingEntry.
export const planManagementApi = {
  list: () => iamApi.get<BillingPlanDetails[]>('/api/v1/iam/billing-plans-management'),
  upsert: (payload: Omit<BillingPlanDetails, 'baseAmountInr' | 'perUserAmountInr'>) =>
    iamApi.put<BillingPlanDetails>('/api/v1/iam/billing-plans-management', payload),
}

export interface TrialSubscription {
  orgId: string
  orgName: string
  planCode: string
  status: 'trial' | 'expired'
  trialEndsAt?: string | null
}

// Superadmin only — orgs on (or lapsed from) a free trial, and per-org
// extensions. A future end date reopens an expired trial.
export const trialManagementApi = {
  list: () => iamApi.get<TrialSubscription[]>('/api/v1/iam/trial-subscriptions'),
  setTrialEnd: (orgId: string, trialEndsAt: string) =>
    iamApi.put<TrialSubscription>(`/api/v1/iam/trial-subscriptions/${encodeURIComponent(orgId)}`, { trialEndsAt }),
}
